import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../../utils/translation";
import {
  listUsers,
  selectUsers,
  selectUsersError,
  selectUsersLoading,
  selectUsersMessage,
  selectUsersUpdateError,
  selectUsersUpdateMessage,
  setUserActiveOptimistic,
  updateUserActive,
} from "../userSlice";

const PAGE_SIZE = 10;

const UsersActivePage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const users = useSelector(selectUsers);
  const adminAccess = useSelector((state) => state.auth?.adminAccess);
  const isLoading = useSelector(selectUsersLoading);
  const isError = useSelector(selectUsersError);
  const message = useSelector(selectUsersMessage);
  const updateError = useSelector(selectUsersUpdateError);
  const updateMessage = useSelector(selectUsersUpdateMessage);
  const [permissionError, setPermissionError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(listUsers());
  }, [dispatch]);

  const getFullName = (user) => {
    if (user?.fullName) return user.fullName;
    const nameParts = [user?.name, user?.surName].filter(Boolean);
    return nameParts.length ? nameParts.join(" ") : "-";
  };

  const getEmail = (user) => {
    return user?.email || user?.userEmail || "-";
  };

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const safeText = text || "";
    const lower = safeText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lower.indexOf(lowerTerm);
    if (index === -1) return safeText || "-";
    const before = safeText.slice(0, index);
    const match = safeText.slice(index, index + term.length);
    const after = safeText.slice(index + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "yellow" }}>
          {match}
        </span>
        {after}
      </>
    );
  };

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users || [];
    return (users || []).filter((user) => {
      const name = getFullName(user).toLowerCase();
      return name.includes(term);
    });
  }, [users, search]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const renderRows = () => {
    if (isLoading && (!users || users.length === 0)) {
      return (
        <tr>
          <td colSpan="3">{t("admin_common.loading")}</td>
        </tr>
      );
    }
    if (isError && (!users || users.length === 0)) {
      return (
        <tr>
          <td colSpan="3" className="text-danger">
            {message || t("admin_common.error_generic")}
          </td>
        </tr>
      );
    }
    if (!users || users.length === 0) {
      return (
        <tr>
          <td colSpan="3">{t("admin_common.no_records")}</td>
        </tr>
      );
    }
    if (!pagedUsers || pagedUsers.length === 0) {
      return (
        <tr>
          <td colSpan="3">{t("admin_common.no_records")}</td>
        </tr>
      );
    }
    return pagedUsers.map((user) => {
      const fullName = getFullName(user);
      return (
      <tr key={user.id || user.userId}>
        <td>{highlightMatch(fullName, search.trim())}</td>
        <td>{getEmail(user)}</td>
        <td>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id={`user-active-${user.id || user.userId}`}
              checked={Boolean(user?.state)}
              onChange={() => {
                if (!adminAccess) {
                  setPermissionError(t("admin_common.permission_denied"));
                  return;
                }
                setPermissionError("");
                const id = user.id || user.userId;
                if (!id) return;
                const nextValue = !user?.state;
                dispatch(
                  setUserActiveOptimistic({
                    id,
                    state: nextValue,
                    updatedAt: Date.now(),
                  })
                );
                dispatch(
                  updateUserActive({
                    id,
                    state: nextValue,
                    prevState: user?.state,
                  })
                );
              }}
            />
            <label
              className="form-check-label"
              htmlFor={`user-active-${user.id || user.userId}`}
            >
              {user?.state ? t("admin_common.status_active") : t("admin_common.status_passive")}
            </label>
          </div>
        </td>
      </tr>
      );
    });
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">{t("admin_users_pages.active_title")}</h4>
              {permissionError && (
                <div className="alert alert-danger">{permissionError}</div>
              )}
              {updateError && (
                <div className="text-danger mb-2">
                  {updateMessage || t("admin_users_pages.update_failed")}
                </div>
              )}
              <div className="row mb-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("admin_common.search_name_placeholder")}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>{t("admin_common.name")}</th>
                      <th>{t("admin_common.email")}</th>
                      <th>{t("admin_common.status")}</th>
                    </tr>
                  </thead>
                  <tbody>{renderRows()}</tbody>
                </table>
              </div>
              <div className="d-flex align-items-center justify-content-between mt-3">
                <span className="text-muted">
                  {t("admin_common.total_records", { count: filteredUsers.length })}
                </span>
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    {t("admin_common.prev")}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" disabled>
                    {currentPage} / {totalPages}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    {t("admin_common.next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UsersActivePage;
